<template>
<div id="user-form">
  <form
    :method="methodType"
    accept-charset="UTF-8"
    autocomplete="off"
    :action="actionUrl"
    enctype="multipart/form-data"
    id="userForm"
    v-on:submit.prevent="submit"
  >
    <div>
      <fieldset class="form-group">

        <legend v-if="userId">Edit User #{{ userId }}</legend>
        <legend v-else>Create User</legend>

        <div class="form-group">
          <label for="user_email" class="control-label required">
            Email
          </label>

          <input type="email"
            name="user[email]"
            class="form-control"
            id="user_email"
            placeholder="Enter email"
            required="true"
            autofocus="true"
            v-model="user.email"
          >
        </div>

        <div class="form-group">
          <label for="user_username" class="control-label required">
            Username
          </label>

          <input type="text"
            name="user[username]"
            class="form-control"
            id="user_username"
            placeholder="Enter username"
            v-model="user.username"
          >
        </div>

        <div class="form-group">
          <label for="user_name" class="control-label">
            Name
          </label>

          <input type="text"
            name="user[name]"
            class="form-control"
            id="user_name"
            placeholder="Enter name"
            v-model="user.name"
          >
        </div>

        <div class="form-group">
          <label for="user_password" class="control-label">
            <span v-if="userId">New</span> Password
          </label>

          <input type="password"
            name="user[password]"
            class="form-control"
            id="user_password"
            placeholder="Enter password"
            v-model="user.password"
          >
        </div>

        <div class="form-group">
          <input type="checkbox"
            name="user[active]"
            id="user_active"
            v-model="user.active"
            v-on:change="validateActive"
            true-value="true"
            false-value="false"
          />

          <label for="user_active" class="control-label">
            Active?
          </label>
        </div>

        <div class="form-group">
          <input type="checkbox"
            name="user[private]"
            id="user_private"
            v-model="user.private"
            v-on:change="validateHidden"
            true-value="true"
            false-value="false"
          />

          <label for="user_private" class="control-label">
            Hidden from other users?
          </label>
        </div>

        <div class="form-group">
          <input type="checkbox"
            name="user[receive_targeted_emails]"
            id="user_receive_targeted_emails"
            v-model="user.receive_targeted_emails"
            true-value="true"
            false-value="false"
          />

          <label for="user_receive_targeted_emails" class="control-label">
            Receives Targeted Emails?
          </label>
        </div>

        <div
          class="form-group"
          v-if="userId && this.$root.isAdmin"
        >
          <input type="checkbox"
            name="user[verified]"
            id="user_verified"
            v-model="user.verified"
            true-value="true"
            false-value="false"
          />

          <label for="user_verified" class="control-label">
            Verified user?
          </label>
        </div>

        <div
          class="form-group"
          v-if="userId && this.$root.isAdmin"
        >
          <input type="checkbox"
            name="user[staff]"
            id="user_staff"
            v-model="user.staff"
            true-value="true"
            false-value="false"
          />

          <label for="user_staff" class="control-label">
            Staff user?
          </label>
        </div>

        <div class="form-group">
          <role-picker
            :roleId="roleId"
            selectName="user[role_id]"
          />
        </div>

        <div
          v-if="isAuthor && hasUserDetails"
        >
          <h3>
            Author Details
          </h3>

          <p
            v-if="userId && this.$root.isAdmin"
          >
            <router-link :to="{
              name: 'edit_user_details',
              params: { userId: userId }
              }"
            >
              Edit Author Details
            </router-link>
          </p>

          <div
            v-if="hasUserDetails"
          >
            <div
              class="form-group"
            >
              <input type="checkbox"
                name="user[featured]"
                id="user_featured"
                v-model="user.featured"
                true-value="true"
                false-value="false"
                v-on:change="validateFeatured"
              />

              <label for="user_featured" class="control-label">
                Featured author?
              </label>
            </div>
          </div>
          <div
            v-else
          >
            Create Author Details
          </div>
        </div>
      </fieldset>

      <div class="form-group">
        <button type="submit" class="btn btn-primary btn-block">
          {{ userId ? 'Edit' : 'Create' }} User
          <spinner
            size="small"
            v-if="loaders.modifyingUser"
          />
        </button>
      </div>

      <div v-if="userId">
        <button type="button" v-on:click="deactivateUser" class="btn btn-danger btn-block">
          Deactivate
        </button>

        <button type="button" v-on:click="deleteUser" class="btn btn-danger btn-block">
          Delete Account
          <spinner
            size="small"
            v-if="loaders.deleteUser"
          />
        </button>
      </div>
    </div>
  </form>
</div>
</template>

<script>
import { mapState } from 'vuex'

import Spinner from 'vue-simple-spinner'

import AuthorForm from '@/components/AuthorForm'
import RolePicker from '@/components/RolePicker'
import UserDetailForm from '@/components/UserDetailForm'

import FlashStore from '@/stores/FlashStore'

export default {
  components: {
    'author-form': AuthorForm,
    'role-picker': RolePicker,
    'spinner': Spinner,
    'user-detail-form': UserDetailForm
  },

  computed: mapState({
    base: state => state.base,
    loaders: state => state.LoaderModule.loaders,
    user: state => state.UserModule.user,

    actionUrl () {
      return this.apiUrl('/users/' + (this.userId ? this.userId : ''))
    },

    hasUserDetails () {
      return this.isAuthor || (
        this.user.userDetails && this.user.userDetails.id
      )
    },

    isAuthor () {
      return this.user.role && this.user.role.attributes.alias === 'author'
    },

    isActive () {
      return this.user.active || false
    },

    isPrivate () {
      return this.user.private || false
    },

    methodType () {
      return this.userId ? 'PUT' : 'POST'
    },

    roleId () {
      if (this.user && this.user.relationships && this.user.relationships.role) {
        return this.user.relationships.role.id
      }
    }
  }),

  props: ['userId'],

  created () {
    // Janky.
    if (this.userId) {
      this.get()
    } else {
      this.$store.dispatch(
        `UserModule/newUser`
      ).catch(this.$root.processErrors)
    }

    this.$on('roleChanged', this.roleChanged)
  },

  methods: {
    deactivateUser (e) {
      if (confirm('Are you sure you want to deactivate this account?')) {
        this.active = false
        this.submit(e)
      }
    },

    deleteUser (e) {
      if (confirm('Are you sure you want to delete this account? This cannot be undone.')) {
        // Double-confirmation.
        if (confirm('Are you absolutely positive you want to delete this account? You cannot undo this action.')) {
          this.delete()
        }
      }
    },

    get () {
      this.$store.dispatch(
        `UserModule/getUser`, this.userId
      ).catch(this.$root.processErrors)
    },

    roleChanged (roleId) {
      this.$store.dispatch(
        `UserModule/setUserRoleId`, roleId
      ).catch(this.$root.processErrors)
    },

    submit (ev) {
      const action = this.userId ? 'updateUser' : 'createUser'
      const actionLbl = this.userId ? 'updated' : 'created'

      this.$store.dispatch(
        `UserModule/${action}`, this.user
      ).then(() => {
        this.$router.push({
          name: 'user',
          params: {
            userId: this.user.id
          }
        })
      }).catch(
        this.$root.processErrors
      ).finally(() => {
        FlashStore.result(`User ${actionLbl}.`)
      })
    },

    validateFeatured (ev) {
      this.validateUserState(
        'featured', 'private',
        'This user is hidden from the public and cannot be featured. ' +
        "Please change the user's visibility to allow them to be featured."
      ).then(() => {
        ev.target.checked = false
      })
    },

    validateHidden (ev) {
      this.validateUserState(
        'private', 'featured',
        'This user is a featured user and must be visible to all users. ' +
        "Please change the user's featured state to allow them to be hidden."
      ).then(() => {
        ev.target.checked = false
      })
    },

    validateActive (ev) {
      this.validateUserState(
        'inactive', 'featured',
        'This user is a featured user and must be active. ' +
        "Please change the user's featured state to allow them to be inactive."
      ).then(() => {
        ev.target.checked = true
      })
    },

    validateUserState (attr, compAttr, alertStr) {
      // Compensate for true/false-value vs. user model value.
      if (this.user[compAttr] === 'true' || this.user[compAttr] === true) {
        alert(alertStr)

        return this.$store.dispatch('UserModule/deselectUserAttr', attr)
      }
    }
  },

  watch: {
    password: function (password) {
      this.password = password.trim()
    },

    newPassword: function (newPassword) {
      this.newPassword = newPassword.trim()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/UserForm.vue